import {
  ActionList, ActionListItem, AlertVariant, Button,
  Icon, PageSection,
  Pagination, PaginationVariant, SearchInput, Split, SplitItem, Title, Toolbar,
  ToolbarContent, ToolbarGroup, ToolbarItem, Tooltip
} from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { RemoveButton } from '@app/components/RemoveButton';
import { useEntity } from '@app/hooks/useEntity';
import { Partner } from '@app/model/Partner';
import { usePartnerStore } from '@app/store/entityStore';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

const PartnerOverview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const {items: rows, error, retry} = useEntity(usePartnerStore);
  const remove = usePartnerStore(state => state.delete);

  // The search last typed here, kept only for the trip out to a partner and back.
  const { restored, keep } = useFilterRestore<{ filter: string }>('partners');
  const [filter, setFilter] = React.useState<string>(restored?.filter ?? '');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('partners');
  const [page, setPage] = React.useState<number>(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('partners', 10);

  React.useEffect(() => { keep({ filter }); }, [filter, keep]);

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(() => {
    if (!filter.trim()) return rows;
    const lowerFilter = filter.toLowerCase();
    return rows.filter((p) => p.name.toLowerCase().includes(lowerFilter));
  }, [rows, filter]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  const onSearch = (_event: any, value: string) => {
    setFilter(value);
    setPage(1);
  };

  const handleSetPage = (_evt: any, newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageSelect = (_evt: any, newPerPage: number, newPage: number) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const onRemoveConfirm = async (_event: any, p: Partner) => {
    beginLoading();
    try {
      await remove(p.id);
      addNotification('Partner removed successfully', AlertVariant.success);
    } catch (error: any) {
      addNotification('Error removing partner', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact} itemCount={filteredRows.length} page={page} perPage={perPage}
      onSetPage={handleSetPage} onPerPageSelect={handlePerPageSelect}
      perPageOptions={[{ title: '5', value: 5 }, { title: '10', value: 10 }, { title: '20', value: 20 }, { title: '50', value: 50 }]}
      variant={variant as PaginationVariant}
    />
  );

  return (
    <React.Fragment>
      <PageSection isWidthLimited>
        <Split hasGutter>
          <SplitItem><Title headingLevel="h1" size="4xl">Partners</Title></SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" onClick={() => navigate('/partners/new')}>New Partner</Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection isWidthLimited isFilled>
        <Toolbar id="compact-toolbar">
          <ToolbarContent>
            <ToolbarGroup>
              <ToolbarItem>
                <SearchInput placeholder="Filter by name..." value={filter} onChange={onSearch} onClear={(ev) => onSearch(ev, '')} onSearch={onSearch} />
              </ToolbarItem>
            </ToolbarGroup>
            <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
            gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
        <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
          <InnerScrollContainer>
            <Table aria-label="Partners List" isStickyHeader variant='compact' gridBreakPoint="">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Primary Contact</Th>
                  <Th>Email</Th>
                  <Th>Client Executive</Th>
                  <Th isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row) => {
                  const primaryContact = row.contacts && row.contacts.length > 0 ? row.contacts[0] : null;
                  return (
                    <Tr key={row.id} {...rowProps(row.id)}>
                      <Td dataLabel="Name">{row.name}</Td>
                      <Td dataLabel="Primary Contact">{primaryContact ? primaryContact.name : 'N/A'}</Td>
                      <Td dataLabel="Email">{primaryContact ? primaryContact.email : 'N/A'}</Td>
                      <Td dataLabel="Client Executive">{row.clientExecutive?.name || 'N/A'}</Td>
                      <Td dataLabel="Actions" modifier="nowrap"
                        isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                        <ActionList>
                          <ActionListItem>
                            <Tooltip content="Edit">
                              <Button icon={<Icon size='lg'><PencilAltIcon /></Icon>} variant="plain" onClick={() => { rememberRow(row.id, page); navigate(`/partners/${row.id}`); }} />
                            </Tooltip>
                          </ActionListItem>
                          <ActionListItem>
                            <RemoveButton confirmationMessage={`Confirm removal of "${row.name}"?`} onRemoveConfirm={(ev) => onRemoveConfirm(ev, row)} />
                          </ActionListItem>
                        </ActionList>
                      </Td>
                    </Tr>
                  );
                })}
                {/* Nothing to show because the load failed, rather than because the list is empty */}
                {error && paginatedRows.length === 0 && (
                  <DataLoadErrorRow colSpan={5} error={error} entityLabel="partners" onRetry={retry} />
                )}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
        {renderPagination('bottom', false)}
      </PageSection>
    </React.Fragment>
  );
};

export { PartnerOverview };
